// src/components/AdminDashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMoneyBillWave,
  faUniversity,
  faHandHoldingUsd,
  faPiggyBank,
  faGem,
  faTicketAlt,
  faChartLine,
  faCogs,
  faSignOutAlt,
  faCreditCard,
  faReceipt,
  faBalanceScale,
  faBook,
  faFileAlt,
  faPhone,
  faBars,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import GeneralLoanHome from './GL/GeneralLoanHome';
import CDLoanHome from './CD/CDLoanHome';
import PimgyHome from './Pimgy/PimgyHome';
import ExpenseManager from './AdminDashboard/ExpenseManager';
import BalanceEnquiry from './AdminDashboard/BalanceEnquiry';
import AdminDailyCashbook from './AdminDashboard/AdminDailyCashbook';
import AssistantRegistrar from './AdminDashboard/AssistantRegistrar';
import SheetPage from './AdminDashboard/SheetPage';
import CallInfo from './AdminDashboard/CallInfo';
import LakshmikantDashboard from './AdminDashboard/LakshmikantDashboard';
import { isLakshmikantDashboardUser } from '../constants/adminUsers';
import './AdminDashboard.css';
import { API_BASE_URL } from '../config';
import { getTodayYYYYMMDD } from '../utils/dateUtils';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const adminEmail = user?.email || localStorage.getItem('adminEmail') || '';
  const isLakshmikant = isLakshmikantDashboardUser(adminEmail);
  const [standardBalance, setStandardBalance] = useState(null);
  const [secondaryBalance, setSecondaryBalance] = useState(null);
  const [secondaryOpeningBalance, setSecondaryOpeningBalance] = useState(null);
  const [secondaryClosingBalance, setSecondaryClosingBalance] = useState(null);
  const [isLoadingStandard, setIsLoadingStandard] = useState(true);
  const [isLoadingSecondary, setIsLoadingSecondary] = useState(false);
  const [standardError, setStandardError] = useState('');
  const [secondaryError, setSecondaryError] = useState('');
  const [isSecondaryVisible, setIsSecondaryVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const fetchStandardBalance = useCallback(async () => {
    setIsLoadingStandard(true);
    setStandardError('');
    try {
      const response = await fetch(`${API_BASE_URL}/balance-enquiry`);
      if (response.ok) {
        const data = await response.json();
        setStandardBalance(data.overallBalance ?? null);
      } else {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch overall balance.');
      }
    } catch (error) {
      console.error('Error fetching overall balance:', error);
      setStandardError(error.message || 'Unable to load overall balance.');
      setStandardBalance(null);
    } finally {
      setIsLoadingStandard(false);
    }
  }, []);

  const fetchSecondaryBalance = useCallback(async () => {
    setIsLoadingSecondary(true);
    setSecondaryError('');
    try {
      const today = getTodayYYYYMMDD();
      const response = await fetch(
        `${API_BASE_URL}/api/secondary-balance-enquiry/daily-balances?date=${today}`
      );
      if (response.ok) {
        const data = await response.json();
        setSecondaryOpeningBalance(data.secondaryOpeningBalance ?? null);
        setSecondaryClosingBalance(data.secondaryClosingBalance ?? null);
        setSecondaryBalance(data.secondaryDayNet ?? null);
      } else {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch secondary balance.');
      }
    } catch (error) {
      console.error('Error fetching secondary balance:', error);
      setSecondaryError(error.message || 'Unable to load secondary balance.');
      setSecondaryBalance(null);
      setSecondaryOpeningBalance(null);
      setSecondaryClosingBalance(null);
    } finally {
      setIsLoadingSecondary(false);
    }
  }, []);

  useEffect(() => {
    fetchStandardBalance();
  }, [fetchStandardBalance]);

  useEffect(() => {
    if (isLakshmikant) {
      return undefined;
    }

    const handleShortcut = (event) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'q') {
        event.preventDefault();
        setIsSecondaryVisible((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [isLakshmikant]);

  useEffect(() => {
    if (isLakshmikant) {
      return;
    }

    if (isSecondaryVisible) {
      fetchSecondaryBalance();
    } else {
      setSecondaryBalance(null);
      setSecondaryOpeningBalance(null);
      setSecondaryClosingBalance(null);
      setSecondaryError('');
      setIsLoadingSecondary(false);
    }
  }, [isLakshmikant, isSecondaryVisible, fetchSecondaryBalance]);

  const handleLogout = () => {
    logout();
    navigate('/WelcomePage');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getBalanceColor = (balance) => {
    if (balance === null || balance === undefined) return '#6c757d';
    return balance >= 0 ? '#28a745' : '#dc3545';
  };

  return (
    <Box className="dashboard-container">
      <Box className="mobile-topbar">
        <button
          type="button"
          className="mobile-menu-btn"
          onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
        </button>
        <Typography variant="h6" component="p" className="mobile-topbar-title">
          Admin Dashboard
        </Typography>
      </Box>

      {isMobileMenuOpen ? (
        <Box
          className="sidebar-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      ) : null}

      <Box component="aside" className={`sidebar ${isMobileMenuOpen ? 'sidebar-open' : ''}`}>
        <Box className="sidebar-header">
          <Typography variant="h5" component="h2" className="sidebar-title">
            Admin Dashboard
          </Typography>
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </Box>
        <nav>
          <ul>
            {isLakshmikant && (
              <li>
                <NavLink to="/admin-dashboard" end activeClassName="active-link">
                  <FontAwesomeIcon icon={faChartLine} /> Dashboard
                </NavLink>
              </li>
            )}
            <li>
              <NavLink to="/general-loan" activeClassName="active-link">
                <FontAwesomeIcon icon={faMoneyBillWave} /> General Loan
              </NavLink>
            </li>
            <li>
              <NavLink to="/cd-loan" activeClassName="active-link">
                <FontAwesomeIcon icon={faUniversity} /> CD Loan
              </NavLink>
            </li>
            <li>
              <NavLink to="/tbd-loan" activeClassName="active-link">
                <FontAwesomeIcon icon={faHandHoldingUsd} /> TBD Loan
              </NavLink>
            </li>
            <li>
              <NavLink to="/fd" activeClassName="active-link">
                <FontAwesomeIcon icon={faCreditCard} /> RD Component
              </NavLink>
            </li>
            <li>
              <NavLink to="/expense-manager" activeClassName="active-link">
                <FontAwesomeIcon icon={faReceipt} /> Expense Manager
              </NavLink>
            </li>
            <li>
              <NavLink to="/balance-enquiry" activeClassName="active-link">
                <FontAwesomeIcon icon={faBalanceScale} /> Balance Enquiry
              </NavLink>
            </li>
            <li>
              <NavLink to="/daily-cashbook" activeClassName="active-link">
                <FontAwesomeIcon icon={faBook} /> Daily Cashbook
              </NavLink>
            </li>
            <li>
              <NavLink to="/pigmy-collection" activeClassName="active-link">
                <FontAwesomeIcon icon={faPiggyBank} /> Pigmy Collection
              </NavLink>
            </li>
            <li>
              <NavLink to="/sheets" activeClassName="active-link">
                <FontAwesomeIcon icon={faChartLine} /> Sheets
              </NavLink>
            </li>
            <li>
              <NavLink to="/assistant-registrar" activeClassName="active-link">
                <FontAwesomeIcon icon={faFileAlt} /> Assistant Registrar
              </NavLink>
            </li>
            <li>
              <NavLink to="/call-info" activeClassName="active-link">
                <FontAwesomeIcon icon={faPhone} /> Call Info
              </NavLink>
            </li>
          </ul>
        </nav>

        <Button
          variant="contained"
          color="primary"
          className="logout-button"
          onClick={handleLogout}
          startIcon={<FontAwesomeIcon icon={faSignOutAlt} />}
        >
          Logout
        </Button>
      </Box>

      <Box component="main" className="content">
        {/* Overall Balance Display */}
        <Box className="overall-balance-widget">
          <Typography variant="h6" className="balance-label">
            Overall Balance
          </Typography>
          {isLoadingStandard ? (
            <Typography 
              variant="h4" 
              className="balance-amount" 
              style={{ color: '#6c757d' }}
            >
              Loading...
            </Typography>
          ) : standardError ? (
            <Typography 
              variant="body1" 
              className="balance-amount" 
              style={{ color: '#dc3545' }}
            >
              {standardError}
            </Typography>
          ) : (
            <Typography 
              variant="h4" 
              className="balance-amount" 
              style={{ color: getBalanceColor(standardBalance) }}
            >
              {standardBalance !== null ? formatCurrency(standardBalance) : 'N/A'}
            </Typography>
          )}
        </Box>

        {isSecondaryVisible && !isLakshmikant && (
          <Box className="secondary-daily-balance-row">
            <Box className="overall-balance-widget">
              <Typography variant="h6" className="balance-label">
                Opening Balance (Today)
              </Typography>
              {isLoadingSecondary ? (
                <Typography variant="h4" className="balance-amount" style={{ color: '#6c757d' }}>
                  Loading...
                </Typography>
              ) : secondaryError ? (
                <Typography variant="body1" className="balance-amount" style={{ color: '#dc3545' }}>
                  {secondaryError}
                </Typography>
              ) : (
                <Typography
                  variant="h4"
                  className="balance-amount"
                  style={{ color: getBalanceColor(secondaryOpeningBalance) }}
                >
                  {secondaryOpeningBalance !== null ? formatCurrency(secondaryOpeningBalance) : 'N/A'}
                </Typography>
              )}
            </Box>

            <Box className="overall-balance-widget">
              <Typography variant="h6" className="balance-label">
                Closing Balance (Today)
              </Typography>
              {isLoadingSecondary ? (
                <Typography variant="h4" className="balance-amount" style={{ color: '#6c757d' }}>
                  Loading...
                </Typography>
              ) : secondaryError ? (
                <Typography variant="body1" className="balance-amount" style={{ color: '#dc3545' }}>
                  {secondaryError}
                </Typography>
              ) : (
                <Typography
                  variant="h4"
                  className="balance-amount"
                  style={{ color: getBalanceColor(secondaryClosingBalance) }}
                >
                  {secondaryClosingBalance !== null ? formatCurrency(secondaryClosingBalance) : 'N/A'}
                </Typography>
              )}
            </Box>

            <Box className="overall-balance-widget secondary-net-widget">
              <Typography variant="h6" className="balance-label">
                Today&apos;s Net (Secondary)
              </Typography>
              {isLoadingSecondary ? (
                <Typography variant="h4" className="balance-amount" style={{ color: '#6c757d' }}>
                  Loading...
                </Typography>
              ) : secondaryError ? null : (
                <Typography
                  variant="h5"
                  className="balance-amount"
                  style={{ color: getBalanceColor(secondaryBalance) }}
                >
                  {secondaryBalance !== null ? formatCurrency(secondaryBalance) : 'N/A'}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <Routes>
          {isLakshmikant && (
            <Route index element={<LakshmikantDashboard />} />
          )}
          <Route path="/general-loan/*" element={<GeneralLoanHome />} />
          <Route path="/cd-loan" element={<CDLoanHome />} />
          <Route path="/tbd-loan" element={<Typography variant="h6">TBD Loan</Typography>} />
          <Route path="/expense-manager" element={<ExpenseManager />} />
          <Route path="/balance-enquiry" element={<BalanceEnquiry />} />
          <Route path="/daily-cashbook" element={<AdminDailyCashbook />} />
          <Route path="/pigmy-collection/*" element={<PimgyHome />} />
          <Route path="/assistant-registrar" element={<AssistantRegistrar />} />
          <Route path="/sheets" element={<SheetPage />} />
          <Route path="/call-info" element={<CallInfo />} />
        </Routes>
      </Box>
    </Box>
  );
};

export default AdminDashboard;
